/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IAwardKitsBenefitDetail } from "Interfaces/Benefits/IAwardKitsBenefitDetail";
import { IM365BenefitDetail } from "Interfaces/Benefits/IM365BenefitDetail";
import { IMicrosoft365BenefitDetail } from "Interfaces/Benefits/IMicrosoft365BenefitDetail";
import { IProfileBenefitResult } from "Interfaces/Benefits/IProfileBenefitResult";
import { ITeamsBenefitDetail } from "Interfaces/Benefits/ITeamsBenefitDetail";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IMVPProgramProfile } from "Interfaces/MVP/IMVPProgramProfile";
import { IMVPProgramProfileUpdateRequest } from "Interfaces/MVP/IMVPProgramProfileUpdateRequest";
import { IMVPRetireProfileRequest } from "Interfaces/MVP/IMVPRetireProfileRequest";
import { IMVPRetireProfileResponse } from "Interfaces/MVP/IMVPRetireProfileResponse";
import { IMVPUserProfile } from "Interfaces/MVP/IMVPUserProfile";
import { IUserProfileComment } from "Interfaces/UserProfile/IUserProfileComment";
import { IMVPAzureBenefitClaim } from "Interfaces/MVP/IMVPAzureBenefitClaim";

import {
  MVP_ADD_COMMENT,
  MVP_ADD_COMMENT_FAIL,
  MVP_ADD_COMMENT_SUCCESS,
  MVP_CLAIM_BENEFIT_MANAGER,
  MVP_CLAIM_BENEFIT,
  MVP_CLAIM_BENEFIT_FAIL,
  MVP_CLAIM_BENEFIT_RESET,
  MVP_CLAIM_BENEFIT_SUCCESS,
  MVP_EXTEND_AZURE_BENEFIT_CREDIT,
  MVP_EXTEND_AZURE_BENEFIT_CREDIT_FAILURE,
  MVP_EXTEND_AZURE_BENEFIT_CREDIT_RESET,
  MVP_EXTEND_AZURE_BENEFIT_CREDIT_SUCCESS,
  MVP_FETCH_COMMENTS,
  MVP_FETCH_COMMENTS_FAIL,
  MVP_FETCH_COMMENTS_SUCCESS,
  MVP_FETCH_PROFILE_BENEFITS,
  MVP_FETCH_PROFILE_BENEFITS_FAIL,
  MVP_FETCH_PROFILE_BENEFITS_SUCCESS,
  MVP_FETCH_PROGRAM_PROFILE,
  MVP_FETCH_PROGRAM_PROFILE_FAIL,
  MVP_FETCH_PROGRAM_PROFILE_SUCCESS,
  MVP_FETCH_RETIRE_PROFILE,
  MVP_FETCH_RETIRE_PROFILE_FAIL,
  MVP_FETCH_RETIRE_PROFILE_SUCCESS,
  MVP_FETCH_USER_PROFILE,
  MVP_FETCH_USER_PROFILE_FAIL,
  MVP_FETCH_USER_PROFILE_SUCCESS,
  MVP_GET_AWARD_KITS_BENEFIT_DETAIL,
  MVP_GET_AWARD_KITS_BENEFIT_DETAIL_FAILURE,
  MVP_GET_AWARD_KITS_BENEFIT_DETAIL_SUCCESS,
  MVP_GET_M365_BENEFIT_DETAIL,
  MVP_GET_M365_BENEFIT_DETAIL_FAILURE,
  MVP_GET_M365_BENEFIT_DETAIL_SUCCESS,
  MVP_GET_MS365_BENEFIT_DETAIL,
  MVP_GET_MS365_BENEFIT_DETAIL_FAILURE,
  MVP_GET_MS365_BENEFIT_DETAIL_SUCCESS,
  MVP_GET_TEAMS_BENEFIT_DETAIL,
  MVP_GET_TEAMS_BENEFIT_DETAIL_FAILURE,
  MVP_GET_TEAMS_BENEFIT_DETAIL_SUCCESS,
  MVP_RESET_M365_BENEFIT_DETAIL,
  MVP_RESET_MS365_BENEFIT_DETAIL,
  MVP_RETIRE_PROFILE,
  MVP_RETIRE_PROFILE_FAIL,
  MVP_RETIRE_PROFILE_SUCCESS,
  MVP_UPDATE_M365_BENEFIT_DETAIL,
  MVP_UPDATE_M365_BENEFIT_DETAIL_FAILURE,
  MVP_UPDATE_M365_BENEFIT_DETAIL_SUCCESS,
  MVP_UPDATE_MS365_BENEFIT_DETAIL,
  MVP_UPDATE_MS365_BENEFIT_DETAIL_FAILURE,
  MVP_UPDATE_MS365_BENEFIT_DETAIL_SUCCESS,
  MVP_UPDATE_PROGRAM_PROFILE,
  MVP_UPDATE_PROGRAM_PROFILE_FAIL,
  MVP_UPDATE_PROGRAM_PROFILE_RESET,
  MVP_UPDATE_PROGRAM_PROFILE_SUCCESS,
  MVP_USER_PROFILE_FILTER_VALUE_RESET,
  MVP_USER_PROFILE_SEARCH_CURRENT_PAGE_UPDATE,
  MVP_USER_PROFILE_SEARCH_FILTER_VALUE_ADD,
  MVP_USER_PROFILE_SEARCH_FILTER_VALUE_REMOVE,
  MVP_USER_PROFILE_SEARCH_FILTER_VALUE_STATE_PROVINCE_REMOVE,
  MVP_USER_PROFILE_SEARCH_FILTER_VALUE_UPDATE,
  MVP_USER_PROFILE_SEARCH_FILTER_VALUES_BULK_REMOVE,
  MVP_USER_PROFILE_SEARCH_PAGE_SIZE_UPDATE,
  MVP_USER_PROFILE_SEARCH_SORTING_UPDATE,
  MVP_USER_PROFILES_SEARCH,
  MVP_USER_PROFILES_SEARCH_FAIL,
  MVP_USER_PROFILES_SEARCH_SUCCESS,
  MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION,
  MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_FAIL,
  MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_SUCCESS,
  MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING,
  MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL,
  MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS,
  MVP_USERPROFILE_UPDATE_IS_PRIVATE,
  MVP_USERPROFILE_UPDATE_IS_PRIVATE_FAIL,
  MVP_USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS,
  MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL,
  MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_FAIL,
  MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_RESET,
  MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS,
  MVP_ADD_AZURE_BENEFIT_DETAIL,
  MVP_ADD_AZURE_BENEFIT_DETAIL_SUCCESS,
  MVP_ADD_AZURE_BENEFIT_DETAIL_FAIL,
  MVP_ADD_AZURE_BENEFIT_RESET,
  MVP_RETIRE_PROFILE_RESET,
} from "./MVPActionTypes";

/**
 * Action to reset the M365 Benefit state.
 * @interface
 */
export interface IMVPResetM365BenefitDetail {
  type: typeof MVP_RESET_M365_BENEFIT_DETAIL;
}

/**
 * Action to get the users M365 Benefit Detail.
 * @interface
 */
export interface IMVPFetchM365BenefitDetail {
  type: typeof MVP_GET_M365_BENEFIT_DETAIL;
  userProfileId: number;
}

/**
 * Action to get the users Teams Benefit Detail.
 * @interface
 */
export interface IMVPFetchTeamsBenefitDetail {
  type: typeof MVP_GET_TEAMS_BENEFIT_DETAIL;
  userProfileId: number;
}

/**
 * Action to get the users Award Kits Benefit Detail.
 * @interface
 */
export interface IMVPFetchAwardKitsBenefitDetail {
  type: typeof MVP_GET_AWARD_KITS_BENEFIT_DETAIL;
  userProfileId: number;
}

/**
 * Action to get Fetch Success.
 * @interface
 */
export interface IMVPFetchM365BenefitDetailSuccess {
  type: typeof MVP_GET_M365_BENEFIT_DETAIL_SUCCESS;
  m365BenefitDetail: IM365BenefitDetail;
}

/**
 * Action to get Fetch Success.
 * @interface
 */
export interface IMVPFetchTeamsBenefitDetailSuccess {
  type: typeof MVP_GET_TEAMS_BENEFIT_DETAIL_SUCCESS;
  teamsBenefitDetail: ITeamsBenefitDetail;
}

/**
 * Action to get Fetch Success.
 * @interface
 */
export interface IMVPFetchAwardKitsBenefitDetailSuccess {
  type: typeof MVP_GET_AWARD_KITS_BENEFIT_DETAIL_SUCCESS;
  awardKitsBenefitDetail: IAwardKitsBenefitDetail;
}

/**
 * Action to get Fetch Failure.
 * @interface
 */
export interface IMVPFetchM365BenefitDetailFailure {
  type: typeof MVP_GET_M365_BENEFIT_DETAIL_FAILURE;
  m365FetchErrorMessage: string;
}

/**
 * Action to get Fetch Failure.
 * @interface
 */
export interface IMVPFetchTeamsBenefitDetailFailure {
  type: typeof MVP_GET_TEAMS_BENEFIT_DETAIL_FAILURE;
  teamsFetchErrorMessage: string;
}

/**
 * Action to get Fetch Failure.
 * @interface
 */
export interface IMVPFetchAwardKitsBenefitDetailFailure {
  awardKitsFetchErrorMessage: string;
  type: typeof MVP_GET_AWARD_KITS_BENEFIT_DETAIL_FAILURE;
  FetchErrorMessage: string;
}

/**
 * Action to get Update M365 Benefit.
 * @interface
 */
export interface IMVPUpdateM365BenefitDetail {
  type: typeof MVP_UPDATE_M365_BENEFIT_DETAIL;
  m365BenefitDetail: IM365BenefitDetail;
}

/**
 * Action to get the update success details.
 * @interface
 */
export interface IMVPUpdateM365BenefitDetailSuccess {
  type: typeof MVP_UPDATE_M365_BENEFIT_DETAIL_SUCCESS;
  m365BenefitDetail: IM365BenefitDetail;
}

/**
 * Action to get the update failure details.
 * @interface
 */
export interface IMVPUpdateM365BenefitDetailFailure {
  type: typeof MVP_UPDATE_M365_BENEFIT_DETAIL_FAILURE;
  m365UpdateErrorMessage: string;
}

/**
 * Action to reset the Microsoft 365 Benefit state.
 * @interface
 */
export interface IMVPResetMS365BenefitDetail {
  type: typeof MVP_RESET_MS365_BENEFIT_DETAIL;
}

/**
 * Action to get the users Microsoft 365 Benefit Detail.
 * @interface
 */
export interface IMVPFetchMS365BenefitDetail {
  type: typeof MVP_GET_MS365_BENEFIT_DETAIL;
  userProfileId: number;
}

/**
 * Action to get Fetch Success.
 * @interface
 */
export interface IMVPFetchMS365BenefitDetailSuccess {
  type: typeof MVP_GET_MS365_BENEFIT_DETAIL_SUCCESS;
  ms365BenefitDetail: IMicrosoft365BenefitDetail;
}

/**
 * Action to get Fetch Failure.
 * @interface
 */
export interface IMVPFetchMS365BenefitDetailFailure {
  type: typeof MVP_GET_MS365_BENEFIT_DETAIL_FAILURE;
  ms365FetchErrorMessage: string;
}

/**
 * Action to get Update Microsoft 365 Benefit.
 * @interface
 */
export interface IMVPUpdateMS365BenefitDetail {
  type: typeof MVP_UPDATE_MS365_BENEFIT_DETAIL;
  ms365BenefitDetail: IMicrosoft365BenefitDetail;
}

/**
 * Action to get the update success details.
 * @interface
 */
export interface IMVPUpdateMS365BenefitDetailSuccess {
  type: typeof MVP_UPDATE_MS365_BENEFIT_DETAIL_SUCCESS;
  ms365BenefitDetail: IMicrosoft365BenefitDetail;
}

/**
 * Action to get the update failure details.
 * @interface
 */
export interface IMVPUpdateMS365BenefitDetailFailure {
  type: typeof MVP_UPDATE_MS365_BENEFIT_DETAIL_FAILURE;
  ms365UpdateErrorMessage: string;
}

/**
 * Action to get Extend Azure Benefit Credit.
 * @interface
 */
export interface IMVPExtendAzureBenefitCredit {
  type: typeof MVP_EXTEND_AZURE_BENEFIT_CREDIT;
  credit: number;
  userProfileId: number;
}

/**
 * Action to get the update success details.
 * @interface
 */
export interface IMVPExtendAzureBenefitCreditSuccess {
  type: typeof MVP_EXTEND_AZURE_BENEFIT_CREDIT_SUCCESS;
  capCredit: number;
}

/**
 * Action to get the update failure details.
 * @interface
 */
export interface IMVPExtendAzureBenefitCreditFailure {
  type: typeof MVP_EXTEND_AZURE_BENEFIT_CREDIT_FAILURE;
  error: string;
}

/**
 * Action to reset Extend Azure Benefit Credit State.
 */
export interface IMVPExtendAzureBenefitCreditReset {
  type: typeof MVP_EXTEND_AZURE_BENEFIT_CREDIT_RESET;
}

/**
 * Action to get profile benefits.
 * @interface
 */
export interface IMVPFetchProfileBenefits {
  type: typeof MVP_FETCH_PROFILE_BENEFITS;
  userProfileId: number;
}

/**
 * Action to get profile benefits success.
 * @interface
 */
export interface IMVPFetchProfileBenefitsSuccess {
  type: typeof MVP_FETCH_PROFILE_BENEFITS_SUCCESS;
  userProfileBenefits: IProfileBenefitResult;
}

/**
 * Action to get profile benefits fail.
 * @interface
 */
export interface IMVPFetchProfileBenefitsFail {
  type: typeof MVP_FETCH_PROFILE_BENEFITS_FAIL;
  profileBenefitsErrorMessage: string;
}

/**
 * Action claiming the benefits for a user profile.
 * Interface will be used for both MVP_CLAIM_BENEFIT and MVP_CLAIM_BENEFIT_MANAGER actions.
 * MVP_CLAIM_BENEFIT - used to claim the manual benefits(Github Pro, Linkein, AwardKit, ...).
 * MVP_CLAIM_BENEFIT_MANAGER - used to claim the benefits those are managed by benefit manager(Copilot for Finance, Copilot for Service, ...).
 * @interface
 */
export interface IMVPClaimBenefit {
  type: typeof MVP_CLAIM_BENEFIT | typeof MVP_CLAIM_BENEFIT_MANAGER;
  userProfileId: number;
  benefitType: string;
  Sku?: string;
}

/**
 * Action claiming the benefits for a user profile succeed.
 * @interface
 */
export interface IMVPClaimBenefitSuccess {
  type: typeof MVP_CLAIM_BENEFIT_SUCCESS;
}

/**
 * Action claiming the benefits for a user profile fail.
 * @interface
 */
export interface IMVPClaimBenefitFail {
  type: typeof MVP_CLAIM_BENEFIT_FAIL;
  error: string;
}

/**
 * Action claiming the benefits for a user profile reset.
 * @interface
 */
export interface IMVPClaimBenefitReset {
  type: typeof MVP_CLAIM_BENEFIT_RESET;
}

/**
 * Action to fetch User Profile comments
 * @interface
 */
export interface IMVPFetchComments {
  type: typeof MVP_FETCH_COMMENTS;
  mvpUserProfileId: number;
}

/**
 * Action to fetch User Profile comments success
 * @interface
 */
export interface IMVPFetchCommentsSuccess {
  type: typeof MVP_FETCH_COMMENTS_SUCCESS;
  userProfileComments: IUserProfileComment[];
}

/**
 * Action to fetch User Profile comments fail
 * @interface
 */
export interface IMVPFetchCommentsFail {
  type: typeof MVP_FETCH_COMMENTS_FAIL;
  errorMessage: string;
}

/**
 * Action to add new comment
 * @interface
 */
export interface IMVPAddNewComment {
  type: typeof MVP_ADD_COMMENT;
  userProfileId: number;
  newComment: string;
  createdBy: string;
}

/**
 * Action: add new comment success
 * @interface
 */
export interface IMVPAddNewCommentSuccess {
  type: typeof MVP_ADD_COMMENT_SUCCESS;
  userProfileId: number;
}

/**
 * Action: add new comment fail
 * @interface
 */
export interface IMVPAddNewCommentFail {
  type: typeof MVP_ADD_COMMENT_FAIL;
  errorMessage: string;
}

/**
 * Action to fetch Program Profile comments
 * @interface
 */
export interface IMVPFetchProgramProfile {
  type: typeof MVP_FETCH_PROGRAM_PROFILE;
  programProfileId: number;
}

/**
 * Action to fetch Program Profile comments
 * @interface
 */
export interface IMVPFetchRetireProfile {
  type: typeof MVP_FETCH_RETIRE_PROFILE;
  userProfileId: number;
}

/**
 * Action to fetch Program Profile success
 * @interface
 */
export interface IMVPFetchProgramProfileSuccess {
  type: typeof MVP_FETCH_PROGRAM_PROFILE_SUCCESS;
  programProfile: IMVPProgramProfile;
}

/**
 * Action to fetch Program Profile success
 * @interface
 */
export interface IMVPFetchRetireProfileSuccess {
  type: typeof MVP_FETCH_RETIRE_PROFILE_SUCCESS;
  retireProfile: IMVPRetireProfileResponse;
}

/**
 * Action to fetch Program Profile fail
 * @interface
 */
export interface IMVPFetchProgramProfileFail {
  type: typeof MVP_FETCH_PROGRAM_PROFILE_FAIL;
  errorMessage: string;
}

/**
 * Action to fetch Program Profile fail
 * @interface
 */
export interface IMVPFetchRetireProfileFail {
  type: typeof MVP_FETCH_RETIRE_PROFILE_FAIL;
  errorMessage: string;
}

/**
 * Action to update program profile.
 * @interface
 */
export interface IMVPUpdateProgramProfile {
  type: typeof MVP_UPDATE_PROGRAM_PROFILE;
  programProfile: IMVPProgramProfileUpdateRequest;
}

/**
 * Action to update program profile.
 * @interface
 */
export interface IMVPRetireProfile {
  type: typeof MVP_RETIRE_PROFILE;
  retireProfile: IMVPRetireProfileRequest;
}

/**
 * Action to update program profile success.
 * @interface
 */
export interface IMVPUpdateProgramProfileSuccess {
  type: typeof MVP_UPDATE_PROGRAM_PROFILE_SUCCESS;
}

/**
 * Action to update program profile success.
 * @interface
 */
export interface IMVPRetireProfileSuccess {
  type: typeof MVP_RETIRE_PROFILE_SUCCESS;
}

/**
 * Action to update program profile fail.
 * @interface
 */
export interface IMVPUpdateProgramProfileFail {
  type: typeof MVP_UPDATE_PROGRAM_PROFILE_FAIL;
  errorMessage: string;
}

/**
 * Action to update program profile fail.
 * @interface
 */
export interface IMVPRetireProfileFail {
  type: typeof MVP_RETIRE_PROFILE_FAIL;
  errorMessage: string;
}

/**
 * Action to reset the update program profile state.
 * @interface
 */
export interface IMVPUpdateProgramProfileReset {
  type: typeof MVP_UPDATE_PROGRAM_PROFILE_RESET;
}

/**
 * Action to update azure Benefit Detail.
 * @interface
 */
export interface IMVPAddAzureBenefitDetail {
  type: typeof MVP_ADD_AZURE_BENEFIT_DETAIL;
  azureBenefit: IMVPAzureBenefitClaim;
  userProfileId: number;
}

/**
 * Action to update azure Benefit Detail success.
 * @interface
 */
export interface IMVPAddAzureBenefitDetailSuccess {
  type: typeof MVP_ADD_AZURE_BENEFIT_DETAIL_SUCCESS;
}

/**
 * Action to update azure Benefit Detail fail.
 * @interface
 */
export interface IMVPAddAzureBenefitDetailFail {
  type: typeof MVP_ADD_AZURE_BENEFIT_DETAIL_FAIL;
  errorMessage: string;
}

export interface IMVPAddAzureBenefitReset {
  type: typeof MVP_ADD_AZURE_BENEFIT_RESET;
}

/**
 * Action to reset the update program profile state.
 * @interface
 */
export interface IMVPRetireProfileReset {
  type: typeof MVP_RETIRE_PROFILE_RESET;
}

/**
 * Action to fetch Program Profile comments
 * @interface
 */
export interface IMVPFetchUserProfile {
  type: typeof MVP_FETCH_USER_PROFILE;
  mvpUserProfileId: number;
}

/**
 * Action to fetch User Profile success
 * @interface
 */
export interface IMVPFetchUserProfileSuccess {
  type: typeof MVP_FETCH_USER_PROFILE_SUCCESS;
  userProfile: IMVPUserProfile;
}

/**
 * Action to fetch User Profile fail
 * @interface
 */
export interface IMVPFetchUserProfileFail {
  type: typeof MVP_FETCH_USER_PROFILE_FAIL;
  errorMessage: string;
}

/**
 * Action for searching user profiles.
 * @interface
 */
export interface IMVPUserProfilesSearch {
  type: typeof MVP_USER_PROFILES_SEARCH;
  searchText: string;
  selectedFilterValues: IFilterValue[];
  searchPage: number;
  pageSize: number;
  sortColumn?: string;
  sortDescending?: boolean;
}

/**
 * Action for when user profiles were searched successfully.
 * @interface
 */
export interface IMVPUserProfilesSearchSuccess {
  type: typeof MVP_USER_PROFILES_SEARCH_SUCCESS;
  userProfiles: IMVPUserProfile[];
  searchPage: number;
  filteredCount: number;
  pageSize: number;
}

/**
 * Action for when searching user profiles failed.
 * @interface
 */
export interface IMVPUserProfilesSearchFail {
  type: typeof MVP_USER_PROFILES_SEARCH_FAIL;
  errorMessage: string;
}

/**
 * Action for adding a filter value to the search.
 * @interface
 */
export interface IMVPUserProfileSearchFilterValueAdd {
  type: typeof MVP_USER_PROFILE_SEARCH_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Action for removing a filter value from the search.
 * @interface
 */
export interface IMVPUserProfileSearchFilterValueRemove {
  type: typeof MVP_USER_PROFILE_SEARCH_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Action for removing several filter values from the search.
 * @interface
 */
export interface IMVPUserProfileSearchFilterValuesBulkRemove {
  type: typeof MVP_USER_PROFILE_SEARCH_FILTER_VALUES_BULK_REMOVE;
  filterValues: IFilterValue[];
}

/**
 * Action for updating a filter value from the search.
 * @interface
 */
export interface IMVPUserProfileSearchFilterValueUpdate {
  type: typeof MVP_USER_PROFILE_SEARCH_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Action for removing the state/province filter value from the search.
 * @interface
 */
export interface IMVPUserProfileSearchFilterValueStateProvinceRemove {
  type: typeof MVP_USER_PROFILE_SEARCH_FILTER_VALUE_STATE_PROVINCE_REMOVE;
}

/**
 * Action for updating the page size for the search.
 * @interface
 */
export interface IMVPUserProfileSearchPageSizeUpdate {
  type: typeof MVP_USER_PROFILE_SEARCH_PAGE_SIZE_UPDATE;
  pageSize: number;
}

/**
 * Action for updating the current page for the search.
 * @interface
 */
export interface IMVPUserProfileSearchCurrentPageUpdate {
  type: typeof MVP_USER_PROFILE_SEARCH_CURRENT_PAGE_UPDATE;
  currentPage: number;
}

/**
 * Action: updating a user's blocked shipping status
 * @interface
 */
export interface IMVPUserProfileUpdateBlockShipping {
  type: typeof MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING;
  isShippingBlocked: boolean;
  userProfileId: number;
}

/**
 * Action: updating a user's blocked shipping status fail
 * @interface
 */
export interface IMVPUserProfileUpdateBlockShippingFail {
  type: typeof MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL;
  errorMessage: string;
}

/**
 * Action: updating a user's blocked shipping status success
 * @interface
 */
export interface IMVPUserProfileUpdateBlockShippingSuccess {
  type: typeof MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS;
  userProfileId: number;
}

/**
 * Action: update user's profile is private flag.
 * @interface
 */
export interface IMVPUserProfileIsPrivateUpdate {
  type: typeof MVP_USERPROFILE_UPDATE_IS_PRIVATE;
  isPrivate: boolean;
  userProfileId: number;
}

/**
 * Action: update user's profile is private flag success.
 * @interface
 */
export interface IMVPUserProfileIsPrivateUpdateSuccess {
  type: typeof MVP_USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS;
}

/**
 * Action: update user's profile is private flag fail.
 * @interface
 */
export interface IMVPUserProfileIsPrivateUpdateFail {
  type: typeof MVP_USERPROFILE_UPDATE_IS_PRIVATE_FAIL;
  error: string;
}

/**
 * Action: update user's profile is Annual Exception.
 * @interface
 */
export interface IMVPUserProfileAnnualExceptionUpdate {
  type: typeof MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION;
  justification: string;
  userProfileId: number;
}

/**
 * Action: update user's profile is Annual Exception success.
 * @interface
 */
export interface IMVPUserProfileAnnualExceptionUpdateSuccess {
  type: typeof MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_SUCCESS;
}

/**
 * Action: update user's profile is Annual Exception fail.
 * @interface
 */
export interface IMVPUserProfileAnnualExceptionUpdateFail {
  type: typeof MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_FAIL;
  error: string;
}

/**
 * Action: update user's profile is private flag fail.
 * @interface
 */
export interface IMVPUserProfileSearchSortingChange {
  type: typeof MVP_USER_PROFILE_SEARCH_SORTING_UPDATE;
  sortColumn?: string;
  sortDescending?: boolean;
}

/**
 * Action: reset mvp user profile filter values.
 * @interface
 */
export interface IMVPUserProfileFilterValueReset {
  type: typeof MVP_USER_PROFILE_FILTER_VALUE_RESET;
}

/**
 * Action: update user's primary email.
 * @interface
 */
export interface IMVPUserProfilePrimaryEmailUpdate {
  type: typeof MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL;
  userProfileId: number;
  newEmail: string;
}

/**
 * Action: update user's primary email fail.
 */
export interface IMVPUserProfilePrimaryEmailUpdateFail {
  type: typeof MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_FAIL;
  errorMessage: string;
}

/**
 * Action: update user's primary email success.
 */
export interface IMVPUserProfilePrimaryEmailUpdateSuccess {
  type: typeof MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS;
}

/**
 * Action: reset user's primary email.
 */
export interface IMVPUserProfilePrimaryEmailUpdateReset {
  type: typeof MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_RESET;
}

/**
 * Action used for reseting the state.
 * @returns {IMVPResetM365BenefitDetail} the action.
 */
export function m365BenefitDetailReset(): IMVPResetM365BenefitDetail {
  return {
    type: MVP_RESET_M365_BENEFIT_DETAIL,
  };
}

/**
 * Action used for fetch M365 Benefit Details.
 * @returns {IMVPFetchM365BenefitDetail} the action.
 */
export function fetchM365BenefitDetail(userProfileId: number): IMVPFetchM365BenefitDetail {
  return {
    type: MVP_GET_M365_BENEFIT_DETAIL,
    userProfileId,
  };
}

/**
 * Action used for fetch success.
 * @returns {IMVPFetchM365BenefitDetailSuccess} the action.
 */
export function fetchM365BenefitDetailSuccess(
  m365BenefitDetail: IM365BenefitDetail
): IMVPFetchM365BenefitDetailSuccess {
  return {
    type: MVP_GET_M365_BENEFIT_DETAIL_SUCCESS,
    m365BenefitDetail,
  };
}

/**
 * Action used for fetch failure.
 * @returns {IMVPFetchM365BenefitDetailFailure} the action.
 */
export function fetchM365BenefitDetailFailure(
  m365FetchErrorMessage: string
): IMVPFetchM365BenefitDetailFailure {
  return {
    type: MVP_GET_M365_BENEFIT_DETAIL_FAILURE,
    m365FetchErrorMessage,
  };
}

/**
 * Action used for fetch Teams Benefit Details.
 * @returns {IMVPFetchTeamsBenefitDetail} the action.
 */
export function fetchTeamsBenefitDetail(userProfileId: number): IMVPFetchTeamsBenefitDetail {
  return {
    type: MVP_GET_TEAMS_BENEFIT_DETAIL,
    userProfileId,
  };
}

/**
 * Action used for fetch success.
 * @returns {IMVPFetchTeamsBenefitDetailSuccess} the action.
 */
export function fetchTeamsBenefitDetailSuccess(
  teamsBenefitDetail: ITeamsBenefitDetail
): IMVPFetchTeamsBenefitDetailSuccess {
  return {
    type: MVP_GET_TEAMS_BENEFIT_DETAIL_SUCCESS,
    teamsBenefitDetail,
  };
}

/**
 * Action used for fetch Teams Benefit Details.
 * @returns {IMVPFetchAwardKitsBenefitDetail} the action.
 */
export function fetchAwardKitsBenefitDetail(
  userProfileId: number
): IMVPFetchAwardKitsBenefitDetail {
  return {
    type: MVP_GET_AWARD_KITS_BENEFIT_DETAIL,
    userProfileId,
  };
}

/**
 * Action used for fetch success.
 * @returns {IMVPFetchAwardKitsBenefitDetailSuccess} the action.
 */
export function fetchAwardKitsBenefitDetailSuccess(
  awardKitsBenefitDetail: IAwardKitsBenefitDetail
): IMVPFetchAwardKitsBenefitDetailSuccess {
  return {
    type: MVP_GET_AWARD_KITS_BENEFIT_DETAIL_SUCCESS,
    awardKitsBenefitDetail,
  };
}

/**
 * Action used for update details.
 * @returns {IMVPUpdateM365BenefitDetail} the action.
 */
export function extendAzureBenefitCredit(
  credit: number,
  userProfileId: number
): IMVPExtendAzureBenefitCredit {
  return {
    type: MVP_EXTEND_AZURE_BENEFIT_CREDIT,
    credit,
    userProfileId,
  };
}

/**
 * Action used for update success.
 * @returns {IMVPUpdateM365BenefitDetailSuccess} the action.
 */
export function extendAzureBenefitCreditSuccess(
  capCredit: number
): IMVPExtendAzureBenefitCreditSuccess {
  return {
    type: MVP_EXTEND_AZURE_BENEFIT_CREDIT_SUCCESS,
    capCredit,
  };
}

/**
 * Action used for update failure.
 * @returns {IMVPUpdateM365BenefitDetailFailure} the action.
 */
export function extendAzureBenefitCreditFailure(
  error: string
): IMVPExtendAzureBenefitCreditFailure {
  return {
    type: MVP_EXTEND_AZURE_BENEFIT_CREDIT_FAILURE,
    error,
  };
}

export function extendAzureBenefitCreditReset(): IMVPExtendAzureBenefitCreditReset {
  return {
    type: MVP_EXTEND_AZURE_BENEFIT_CREDIT_RESET,
  };
}

/**
 * Action used for update details.
 * @returns {IMVPUpdateM365BenefitDetail} the action.
 */
export function updateM365BenefitDetail(
  m365BenefitDetail: IM365BenefitDetail
): IMVPUpdateM365BenefitDetail {
  return {
    type: MVP_UPDATE_M365_BENEFIT_DETAIL,
    m365BenefitDetail,
  };
}

/**
 * Action used for update success.
 * @returns {IMVPUpdateM365BenefitDetailSuccess} the action.
 */
export function updateM365BenefitDetailSuccess(
  m365BenefitDetail: IM365BenefitDetail
): IMVPUpdateM365BenefitDetailSuccess {
  return {
    type: MVP_UPDATE_M365_BENEFIT_DETAIL_SUCCESS,
    m365BenefitDetail,
  };
}

/**
 * Action used for update failure.
 * @returns {IMVPUpdateM365BenefitDetailFailure} the action.
 */
export function updateM365BenefitDetailFailure(
  m365UpdateErrorMessage: string
): IMVPUpdateM365BenefitDetailFailure {
  return {
    type: MVP_UPDATE_M365_BENEFIT_DETAIL_FAILURE,
    m365UpdateErrorMessage,
  };
}

/**
 * Action used for resetting the state.
 * @returns {IMVPResetMS365BenefitDetail} the action.
 */
export function ms365BenefitDetailReset(): IMVPResetMS365BenefitDetail {
  return {
    type: MVP_RESET_MS365_BENEFIT_DETAIL,
  };
}

/**
 * Action used for fetch MS365 Benefit Details.
 * @returns {IMVPFetchMS365BenefitDetail} the action.
 */
export function fetchMS365BenefitDetail(userProfileId: number): IMVPFetchMS365BenefitDetail {
  return {
    type: MVP_GET_MS365_BENEFIT_DETAIL,
    userProfileId,
  };
}

/**
 * Action used for fetch success.
 * @returns {IMVPFetchMS365BenefitDetailSuccess} the action.
 */
export function fetchMS365BenefitDetailSuccess(
  ms365BenefitDetail: IMicrosoft365BenefitDetail
): IMVPFetchMS365BenefitDetailSuccess {
  return {
    type: MVP_GET_MS365_BENEFIT_DETAIL_SUCCESS,
    ms365BenefitDetail,
  };
}

/**
 * Action used for fetch failure.
 * @returns {IMVPFetchMS365BenefitDetailFailure} the action.
 */
export function fetchMS365BenefitDetailFailure(
  ms365FetchErrorMessage: string
): IMVPFetchMS365BenefitDetailFailure {
  return {
    type: MVP_GET_MS365_BENEFIT_DETAIL_FAILURE,
    ms365FetchErrorMessage,
  };
}

/**
 * Action used for update details.
 * @returns {IMVPUpdateMS365BenefitDetail} the action.
 */
export function updateMS365BenefitDetail(
  ms365BenefitDetail: IMicrosoft365BenefitDetail
): IMVPUpdateMS365BenefitDetail {
  return {
    type: MVP_UPDATE_MS365_BENEFIT_DETAIL,
    ms365BenefitDetail,
  };
}

/**
 * Action used for update success.
 * @returns {IMVPUpdateMS365BenefitDetailSuccess} the action.
 */
export function updateMS365BenefitDetailSuccess(
  ms365BenefitDetail: IMicrosoft365BenefitDetail
): IMVPUpdateMS365BenefitDetailSuccess {
  return {
    type: MVP_UPDATE_MS365_BENEFIT_DETAIL_SUCCESS,
    ms365BenefitDetail,
  };
}

/**
 * Action used for update failure.
 * @returns {IMVPUpdateMS365BenefitDetailFailure} the action.
 */
export function updateMS365BenefitDetailFailure(
  ms365UpdateErrorMessage: string
): IMVPUpdateMS365BenefitDetailFailure {
  return {
    type: MVP_UPDATE_MS365_BENEFIT_DETAIL_FAILURE,
    ms365UpdateErrorMessage,
  };
}

/**
 * Action used for getting a profiles benefits.
 * @param userProfileId the userProfileId to get benefits for.
 * @returns {IMVPFetchProfileBenefits} the action.
 */
export function fetchProfileBenefits(userProfileId: number): IMVPFetchProfileBenefits {
  return {
    type: MVP_FETCH_PROFILE_BENEFITS,
    userProfileId,
  };
}

/**
 * Action used for getting a profiles benefits success.
 * @param userProfileBenefits the benefits for the profile.
 * @returns {IMVPFetchProfileBenefitsSuccess} the action.
 */
export function fetchProfileBenefitsSuccess(
  userProfileBenefits: IProfileBenefitResult
): IMVPFetchProfileBenefitsSuccess {
  return {
    type: MVP_FETCH_PROFILE_BENEFITS_SUCCESS,
    userProfileBenefits,
  };
}

/**
 * Action used for getting a profiles benefits fail.
 * @param profileBenefitsErrorMessage the profileBenefitsErrorMessage to set.
 * @returns {IMVPFetchProfileBenefitsFail} the action.
 */
export function fetchProfileBenefitsFail(
  profileBenefitsErrorMessage: string
): IMVPFetchProfileBenefitsFail {
  return {
    type: MVP_FETCH_PROFILE_BENEFITS_FAIL,
    profileBenefitsErrorMessage,
  };
}

/**
 * Action creator for MVP_USERPROFILE_CLAIM_BENEFIT_MANAGER.
 * @param benefitType  The selected benefit type (file-id) to claim.
 * @param userProfileId  The user profile id to claim the benefit for.
 * @returns
 */
export function claimBenefitManager(benefitType: string, userProfileId: number): IMVPClaimBenefit {
  return {
    type: MVP_CLAIM_BENEFIT_MANAGER,
    benefitType,
    userProfileId,
  };
}

/**
 * Action creator for MVP_USERPROFILE_CLAIM_BENEFIT.
 * @param {string} benefitType The selected benefit type to claim.
 * @param {number} userProfileId The user profile id to claim the benefit for.
 * @returns {IMVPClaimBenefit}
 */
export function claimBenefit(
  benefitType: string,
  userProfileId: number,
  Sku: string
): IMVPClaimBenefit {
  return {
    type: MVP_CLAIM_BENEFIT,
    benefitType,
    userProfileId,
    Sku,
  };
}

/**
 * Action creator for MVP_USERPROFILE_CLAIM_BENEFIT_SUCCESS.
 * @returns {IMVPClaimBenefitSuccess}
 */
export function claimBenefitSuccess(): IMVPClaimBenefitSuccess {
  return {
    type: MVP_CLAIM_BENEFIT_SUCCESS,
  };
}

/**
 * Action creator for MVP_USERPROFILE_CLAIM_BENEFIT_FAIL.
 * @param {string} error The error message from the API.
 * @returns {IMVPClaimBenefitFail}
 */
export function claimBenefitFail(error: string): IMVPClaimBenefitFail {
  return {
    type: MVP_CLAIM_BENEFIT_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_CLAIM_BENEFIT_RESET.
 * @returns {IMVPClaimBenefitReset}
 */
export function claimBenefitReset(): IMVPClaimBenefitReset {
  return {
    type: MVP_CLAIM_BENEFIT_RESET,
  };
}

/**
 * Action creator for MVP_FETCH_COMMENTS.
 * @param userProfileId The user profile id.
 */
export function fetchUserProfileComments(userProfileId: number): MVPActionTypes {
  return {
    type: MVP_FETCH_COMMENTS,
    mvpUserProfileId: userProfileId,
  };
}

/**
 * Action creator for MVP_FETCH_COMMENTS_SUCCESS.
 * @param userProfileComments The user profile comments.
 */
export function fetchUserProfileCommentsSuccess(
  userProfileComments: IUserProfileComment[]
): MVPActionTypes {
  return {
    type: MVP_FETCH_COMMENTS_SUCCESS,
    userProfileComments,
  };
}

/**
 * Action creator for MVP_FETCH_COMMENTS_FAIL.
 * @param errorMessage The error message.
 */
export function fetchUserProfileCommentsFail(errorMessage: string): MVPActionTypes {
  return {
    type: MVP_FETCH_COMMENTS_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for MVP_ADD_COMMENT.
 * @param userProfileId The user profile id.
 * @param newComment The new comment.
 * @param createdBy The user who created the comment.
 */
export function addNewComment(
  userProfileId: number,
  newComment: string,
  createdBy: string
): MVPActionTypes {
  return {
    type: MVP_ADD_COMMENT,
    userProfileId,
    newComment,
    createdBy,
  };
}

/**
 * Action creator for MVP_ADD_COMMENT_SUCCESS.
 * @param userProfileId The user profile id.
 */
export function addNewCommentSuccess(userProfileId: number): MVPActionTypes {
  return {
    type: MVP_ADD_COMMENT_SUCCESS,
    userProfileId,
  };
}

/**
 * Action creator for MVP_ADD_COMMENT_FAIL.
 * @param errorMessage The error message.
 */
export function addNewCommentFail(errorMessage: string): MVPActionTypes {
  return {
    type: MVP_ADD_COMMENT_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for MVP_FETCH_PROGRAM_PROFILE.
 * @param programProfileId The program profile id.
 */
export function fetchProgramProfile(programProfileId: number): IMVPFetchProgramProfile {
  return {
    type: MVP_FETCH_PROGRAM_PROFILE,
    programProfileId,
  };
}

/**
 * Action creator for MVP_FETCH_RETIRE_PROFILE.
 * @param userProfileId The program profile id.
 */
export function fetchRetireProfile(userProfileId: number): IMVPFetchRetireProfile {
  return {
    type: MVP_FETCH_RETIRE_PROFILE,
    userProfileId,
  };
}

/**
 * Action creator for MVP_FETCH_PROGRAM_PROFILE_SUCCESS.
 * @param userProfile The user profile.
 */
export function fetchProgramProfileSuccess(
  programProfile: IMVPProgramProfile
): IMVPFetchProgramProfileSuccess {
  return {
    type: MVP_FETCH_PROGRAM_PROFILE_SUCCESS,
    programProfile,
  };
}

/**
 * Action creator for MVP_FETCH_PROGRAM_PROFILE_SUCCESS.
 * @param userProfile The user profile.
 */
export function fetchRetireProfileSuccess(
  retireProfile: IMVPRetireProfileResponse
): IMVPFetchRetireProfileSuccess {
  return {
    type: MVP_FETCH_RETIRE_PROFILE_SUCCESS,
    retireProfile,
  };
}

/**
 * Action creator for MVP_FETCH_PROGRAM_PROFILE_FAIL.
 * @param errorMessage The error message.
 */
export function fetchProgramProfileFail(errorMessage: string): IMVPFetchProgramProfileFail {
  return {
    type: MVP_FETCH_PROGRAM_PROFILE_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for MVP_FETCH_PROGRAM_PROFILE_FAIL.
 * @param errorMessage The error message.
 */
export function fetchRetireProfileFail(errorMessage: string): IMVPFetchRetireProfileFail {
  return {
    type: MVP_FETCH_RETIRE_PROFILE_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for MVP_UPDATE_PROGRAM_PROFILE.
 * @param programProfile The program profile updates
 */
export function updateProgramProfile(
  programProfile: IMVPProgramProfileUpdateRequest
): IMVPUpdateProgramProfile {
  return {
    type: MVP_UPDATE_PROGRAM_PROFILE,
    programProfile,
  };
}

/**
 * Action creator for MVP_UPDATE_PROGRAM_PROFILE.
 * @param programProfile The program profile updates
 */
export function retireProfile(retireProfile: IMVPRetireProfileRequest): IMVPRetireProfile {
  return {
    type: MVP_RETIRE_PROFILE,
    retireProfile,
  };
}

/**
 * Action creator for MVP_UPDATE_PROGRAM_PROFILE_SUCCESS.
 */
export function updateProgramProfileSuccess(): IMVPUpdateProgramProfileSuccess {
  return {
    type: MVP_UPDATE_PROGRAM_PROFILE_SUCCESS,
  };
}

/**
 * Action creator for MVP_UPDATE_PROGRAM_PROFILE_SUCCESS.
 */
export function retireProfileSuccess(): IMVPRetireProfileSuccess {
  return {
    type: MVP_RETIRE_PROFILE_SUCCESS,
  };
}

/**
 * Action creator for MVP_UPDATE_PROGRAM_PROFILE_FAIL.
 * @param errorMessage The error message.
 */
export function updateProgramProfileFail(errorMessage: string): IMVPUpdateProgramProfileFail {
  return {
    type: MVP_UPDATE_PROGRAM_PROFILE_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for MVP_UPDATE_PROGRAM_PROFILE_FAIL.
 * @param errorMessage The error message.
 */
export function retireProfileFail(errorMessage: string): IMVPRetireProfileFail {
  return {
    type: MVP_RETIRE_PROFILE_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for MVP_UPDATE_PROGRAM_PROFILE_RESET.
 * @returns {IMVPUpdateProgramProfileReset}
 */
export function updateMVPProgramProfileReset(): IMVPUpdateProgramProfileReset {
  return {
    type: MVP_UPDATE_PROGRAM_PROFILE_RESET,
  };
}

/**
 * Action creator for MVP_UPDATE_AZURE_BENEFIT_DETAIL.
 * @param azureBenefit The program profile updates
 */
export function addAzureBenefitDetail(
  azureBenefit: IMVPAzureBenefitClaim,
  userProfileId: number
): IMVPAddAzureBenefitDetail {
  return {
    type: MVP_ADD_AZURE_BENEFIT_DETAIL,
    azureBenefit,
    userProfileId,
  };
}

/**
 * Action creator for MVP_UPDATE_AZURE_BENEFIT_DETAIL_SUCCESS.
 */
export function addAzureBenefitDetailSuccess(): IMVPAddAzureBenefitDetailSuccess {
  return {
    type: MVP_ADD_AZURE_BENEFIT_DETAIL_SUCCESS,
  };
}

/**
 * Action creator for MVP_UPDATE_AZURE_BENEFIT_DETAIL_FAIL.
 * @param errorMessage The error message.
 */
export function addAzureBenefitDetailFail(errorMessage: string): IMVPAddAzureBenefitDetailFail {
  return {
    type: MVP_ADD_AZURE_BENEFIT_DETAIL_FAIL,
    errorMessage,
  };
}

export function addAzureBenefitReset(): IMVPAddAzureBenefitReset {
  return {
    type: MVP_ADD_AZURE_BENEFIT_RESET,
  };
}

/**
 * Action creator for MVP_RETIRE_PROFILE_RESET.
 * @returns {IMVPRetireProfileReset}
 */
export function updateMVPRetireProfileReset(): IMVPRetireProfileReset {
  return {
    type: MVP_RETIRE_PROFILE_RESET,
  };
}

/**
 * Action creator for MVP_FETCH_USER_PROFILE.
 * @param userProfileId The user profile id.
 */
export function fetchUserProfile(userProfileId: number): MVPActionTypes {
  return {
    type: MVP_FETCH_USER_PROFILE,
    mvpUserProfileId: userProfileId,
  };
}

/**
 * Action creator for MVP_FETCH_USER_PROFILE_SUCCESS.
 * @param userProfile The user profile.
 */
export function fetchUserProfileSuccess(userProfile: IMVPUserProfile): MVPActionTypes {
  return {
    type: MVP_FETCH_USER_PROFILE_SUCCESS,
    userProfile,
  };
}

/**
 * Action creator for MVP_FETCH_USER_PROFILE_FAIL.
 * @param errorMessage The error message.
 */
export function fetchUserProfileFail(errorMessage: string): MVPActionTypes {
  return {
    type: MVP_FETCH_USER_PROFILE_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for MVP_USER_PROFILES_SEARCH.
 * @param searchText The search text.
 * @param selectedFilterValues The selected filter values.
 * @param searchPage The current page.
 * @param pageSize The number of items per page.
 * @returns The action.
 */
export function searchUserProfiles(
  searchText: string,
  selectedFilterValues: IFilterValue[],
  searchPage: number,
  pageSize: number,
  sortColumn?: string,
  sortDescending?: boolean
): MVPActionTypes {
  return {
    type: MVP_USER_PROFILES_SEARCH,
    searchText,
    selectedFilterValues,
    searchPage,
    pageSize,
    sortColumn,
    sortDescending,
  };
}

/**
 * Action creator for MVP_USER_PROFILES_SEARCH_SUCCESS.
 * @param filteredCount The total number of results.
 * @param searchPage The current page.
 * @param pageSize The number of items per page.
 * @param userProfiles The user profiles.
 */
export function searchUserProfileSuccess(
  filteredCount: number,
  searchPage: number,
  pageSize: number,
  userProfiles: IMVPUserProfile[]
): MVPActionTypes {
  return {
    type: MVP_USER_PROFILES_SEARCH_SUCCESS,
    filteredCount,
    searchPage,
    pageSize,
    userProfiles,
  };
}

/**
 * Action creator for MVP_USER_PROFILES_SEARCH_FAIL.
 * @param errorMessage The error message.
 */
export function searchUserProfilesFail(errorMessage: string): MVPActionTypes {
  return {
    type: MVP_USER_PROFILES_SEARCH_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for MVP_USER_PROFILE_SEARCH_FILTER_VALUE_ADD.
 * @param filterValue The filter value.
 */
export function userProfileSearchFilterValueAdd(filterValue: IFilterValue): MVPActionTypes {
  return {
    type: MVP_USER_PROFILE_SEARCH_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for MVP_USER_PROFILE_SEARCH_FILTER_VALUE_REMOVE.
 * @param filterValue The filter value.
 */
export function userProfileSearchFilterValueRemove(filterValue: IFilterValue): MVPActionTypes {
  return {
    type: MVP_USER_PROFILE_SEARCH_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for MVP_USER_PROFILE_SEARCH_FILTER_VALUES_BULK_REMOVE.
 * @param filterValue The filter value.
 */
export function userProfileSearchFilterValuesBulkRemove(
  filterValues: IFilterValue[]
): MVPActionTypes {
  return {
    type: MVP_USER_PROFILE_SEARCH_FILTER_VALUES_BULK_REMOVE,
    filterValues,
  };
}

/**
 * Action creator for MVP_USER_PROFILE_SEARCH_FILTER_VALUE_STATE_PROVINCE_REMOVE.
 */
export function userProfileSearchFilterValueStateProvinceRemove(): MVPActionTypes {
  return {
    type: MVP_USER_PROFILE_SEARCH_FILTER_VALUE_STATE_PROVINCE_REMOVE,
  };
}

/**
 * Action creator for MVP_USER_PROFILE_SEARCH_FILTER_VALUE_UPDATE.
 * @param filterValue The filter value.
 */
export function userProfileSearchFilterValueUpdate(filterValue: IFilterValue): MVPActionTypes {
  return {
    type: MVP_USER_PROFILE_SEARCH_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

/**
 * Action creator for MVP_USER_PROFILE_SEARCH_CURRENT_PAGE_UPDATE.
 * @param filterValue The filter value.
 */
export function userProfileSearchCurrentPageUpdate(currentPage: number): MVPActionTypes {
  return {
    type: MVP_USER_PROFILE_SEARCH_CURRENT_PAGE_UPDATE,
    currentPage,
  };
}

/**
 * Action creator for MVP_USER_PROFILE_SEARCH_PAGE_SIZE_UPDATE.
 * @param filterValue The filter value.
 */
export function userProfileSearchPageSizeUpdate(pageSize: number): MVPActionTypes {
  return {
    type: MVP_USER_PROFILE_SEARCH_PAGE_SIZE_UPDATE,
    pageSize,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_BLOCK_SHIPPING.
 * @returns {IMVPUserProfileUpdateBlockShipping}
 */
export function userProfileUpdateBlockShipping(
  isShippingBlocked: boolean,
  userProfileId: number
): IMVPUserProfileUpdateBlockShipping {
  return {
    type: MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING,
    userProfileId,
    isShippingBlocked,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL.
 * @returns {IMVPUserProfileUpdateBlockShippingFail}
 */
export function userProfileUpdateBlockShippingFail(
  errorMessage: string
): IMVPUserProfileUpdateBlockShippingFail {
  return {
    type: MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS.
 * @returns {IMVPUserProfileUpdateBlockShippingSuccess}
 */
export function userProfileUpdateBlockShippingSuccess(
  userProfileId: number
): IMVPUserProfileUpdateBlockShippingSuccess {
  return {
    type: MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS,
    userProfileId,
  };
}

/**
 * Action creator for MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION.
 * @returns {IMVPUserProfileAnnualExceptionUpdate}
 */
export function userProfileAnnualExceptionUpdate(
  justification: string,
  userProfileId: number
): IMVPUserProfileAnnualExceptionUpdate {
  return {
    type: MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION,
    justification,
    userProfileId,
  };
}

/**
 * Action creator for MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_SUCCESS.
 * @returns {IMVPUserProfileAnnualExceptionUpdateSuccess}
 */
export function userProfileAnnualExceptionUpdateSuccess(): IMVPUserProfileAnnualExceptionUpdateSuccess {
  return {
    type: MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_SUCCESS,
  };
}

/**
 * Action creator for MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_FAIL.
 * @returns {IMVPUserProfileAnnualExceptionUpdateFail}
 */
export function userProfileAnnualExceptionUpdateFail(
  error: string
): IMVPUserProfileAnnualExceptionUpdateFail {
  return {
    type: MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_IS_PRIVATE.
 * @returns {IMVPUserProfileIsPrivateUpdate}
 */
export function userProfileIsPrivateUpdate(
  isPrivate: boolean,
  userProfileId: number
): IMVPUserProfileIsPrivateUpdate {
  return {
    type: MVP_USERPROFILE_UPDATE_IS_PRIVATE,
    isPrivate,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS.
 * @returns {IMVPUserProfileIsPrivateUpdateSuccess}
 */
export function userProfileIsPrivateUpdateSuccess(): IMVPUserProfileIsPrivateUpdateSuccess {
  return {
    type: MVP_USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_IS_PRIVATE_FAIL.
 * @returns {IMVPUserProfileIsPrivateUpdateFail}
 */
export function userProfileIsPrivateUpdateFail(error: string): IMVPUserProfileIsPrivateUpdateFail {
  return {
    type: MVP_USERPROFILE_UPDATE_IS_PRIVATE_FAIL,
    error,
  };
}

/**
 * Action creator for MVP_USER_PROFILE_SEARCH_SORTING_UPDATE.
 * @returns {IMVPUserProfileSearchSortingChange}
 */
export function userProfileSearchSortingUpdate(
  sortColumn?: string,
  sortDescending?: boolean
): IMVPUserProfileSearchSortingChange {
  return {
    type: MVP_USER_PROFILE_SEARCH_SORTING_UPDATE,
    sortColumn,
    sortDescending,
  };
}

/**
 * Action creator for MVP_USER_PROFILE_FILTER_VALUE_RESET.
 * @returns {IMVPUserProfileFilterValueReset}
 */
export function userProfileFilterValueReset(): IMVPUserProfileFilterValueReset {
  return {
    type: MVP_USER_PROFILE_FILTER_VALUE_RESET,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_PRIMARY_EMAIL.
 * @returns {IMVPUserProfilePrimaryEmailUpdate}
 */
export function userProfilePrimaryEmailUpdate(
  userProfileId: number,
  newEmail: string
): IMVPUserProfilePrimaryEmailUpdate {
  return {
    type: MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL,
    userProfileId,
    newEmail,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_PRIMARY_EMAIL_FAIL.
 * @returns {IMVPUserProfilePrimaryEmailFailUpdate}
 */
export function userProfilePrimaryEmailUpdateFail(
  errorMessage: string
): IMVPUserProfilePrimaryEmailUpdateFail {
  return {
    type: MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS.
 * @returns {IMVPUserProfilePrimaryEmailUpdateSuccess}
 */
export function userProfilePrimaryEmailUpdateSuccess(): IMVPUserProfilePrimaryEmailUpdateSuccess {
  return {
    type: MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_PRIMARY_EMAIL_RESET.
 * @returns {IMVPUserProfilePrimaryEmailUpdateReset}
 */
export function userProfilePrimaryEmailUpdateReset(): IMVPUserProfilePrimaryEmailUpdateReset {
  return {
    type: MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_RESET,
  };
}

export type MVPActionTypes =
  | IMVPAddNewComment
  | IMVPAddNewCommentFail
  | IMVPAddNewCommentSuccess
  | IMVPClaimBenefit
  | IMVPClaimBenefitFail
  | IMVPClaimBenefitReset
  | IMVPClaimBenefitSuccess
  | IMVPFetchComments
  | IMVPFetchCommentsFail
  | IMVPFetchCommentsSuccess
  | IMVPFetchProfileBenefits
  | IMVPFetchProfileBenefitsFail
  | IMVPFetchProfileBenefitsSuccess
  | IMVPFetchUserProfile
  | IMVPFetchUserProfileFail
  | IMVPFetchUserProfileSuccess
  | IMVPUserProfileSearchCurrentPageUpdate
  | IMVPUserProfileSearchFilterValueAdd
  | IMVPUserProfileSearchFilterValueRemove
  | IMVPUserProfileSearchFilterValuesBulkRemove
  | IMVPUserProfileSearchFilterValueStateProvinceRemove
  | IMVPUserProfileSearchFilterValueUpdate
  | IMVPUserProfileSearchPageSizeUpdate
  | IMVPUserProfilesSearch
  | IMVPUserProfilesSearchFail
  | IMVPUserProfilesSearchSuccess
  | IMVPFetchUserProfileFail
  | IMVPFetchProgramProfile
  | IMVPFetchProgramProfileFail
  | IMVPFetchProgramProfileSuccess
  | IMVPUpdateProgramProfile
  | IMVPUpdateProgramProfileFail
  | IMVPUpdateProgramProfileSuccess
  | IMVPUpdateProgramProfileReset
  | IMVPResetM365BenefitDetail
  | IMVPFetchM365BenefitDetail
  | IMVPFetchM365BenefitDetailFailure
  | IMVPFetchM365BenefitDetailSuccess
  | IMVPUpdateM365BenefitDetail
  | IMVPUpdateM365BenefitDetailFailure
  | IMVPUpdateM365BenefitDetailSuccess
  | IMVPResetMS365BenefitDetail
  | IMVPFetchMS365BenefitDetail
  | IMVPFetchMS365BenefitDetailFailure
  | IMVPFetchMS365BenefitDetailSuccess
  | IMVPUpdateMS365BenefitDetail
  | IMVPUpdateMS365BenefitDetailFailure
  | IMVPUpdateMS365BenefitDetailSuccess
  | IMVPFetchTeamsBenefitDetail
  | IMVPFetchTeamsBenefitDetailFailure
  | IMVPFetchTeamsBenefitDetailSuccess
  | IMVPFetchAwardKitsBenefitDetail
  | IMVPFetchAwardKitsBenefitDetailFailure
  | IMVPFetchAwardKitsBenefitDetailSuccess
  | IMVPUserProfileUpdateBlockShipping
  | IMVPUserProfileUpdateBlockShippingFail
  | IMVPUserProfileUpdateBlockShippingSuccess
  | IMVPExtendAzureBenefitCredit
  | IMVPExtendAzureBenefitCreditFailure
  | IMVPExtendAzureBenefitCreditSuccess
  | IMVPExtendAzureBenefitCreditReset
  | IMVPUserProfileIsPrivateUpdate
  | IMVPUserProfileIsPrivateUpdateSuccess
  | IMVPUserProfileIsPrivateUpdateFail
  | IMVPUserProfileAnnualExceptionUpdate
  | IMVPUserProfileAnnualExceptionUpdateSuccess
  | IMVPUserProfileAnnualExceptionUpdateFail
  | IMVPUserProfileSearchSortingChange
  | IMVPUserProfileFilterValueReset
  | IMVPUserProfilePrimaryEmailUpdate
  | IMVPUserProfilePrimaryEmailUpdateFail
  | IMVPUserProfilePrimaryEmailUpdateSuccess
  | IMVPUserProfilePrimaryEmailUpdateReset
  | IMVPFetchRetireProfile
  | IMVPFetchRetireProfileFail
  | IMVPFetchRetireProfileSuccess
  | IMVPRetireProfileReset
  | IMVPRetireProfile
  | IMVPRetireProfileSuccess
  | IMVPRetireProfileFail
  | IMVPAddAzureBenefitDetail
  | IMVPAddAzureBenefitDetailSuccess
  | IMVPAddAzureBenefitDetailFail
  | IMVPAddAzureBenefitReset;
